import { useMemo, useState } from 'react';

import { STORAGE_KEYS } from '@constants/storage';
import { DateRangeType } from '@features/dashboard/types';
import { IdLabelValue } from '@types';
import { formatISODate } from '@utils/date';
import { buildQueryString } from '@utils/queryStringBuilder';
import { getLocalStorageItem } from '@utils/storage';
import { StatusEntity } from 'types/lead-management';

import { LeadSource, leadSourceOptions } from '../constants';
import {
  useGetAgentsQuery,
  useGetStatusOptionsQuery,
  useGetTagsQuery
} from '../queries/LeadManagementQuery';

export type LeadListFilters = {
  search?: string;
  source?: string;
  status?: string;
  tags?: string;
  assignee?: string;
  start_date?: string;
  end_date?: string;
};

export type UseLeadListFiltersResult = {
  search: string;
  setSearch: (s: string) => void;
  selectedSources: string[];
  setSelectedSources: (s: string[]) => void;
  selectedStatusIds: string[];
  setSelectedStatusIds: (s: string[]) => void;
  selectedTagIds: string[];
  setSelectedTagIds: (t: string[]) => void;
  selectedDateRange: DateRangeType;
  sourceOptions: IdLabelValue[];
  statusOptions: IdLabelValue[];
  tagOptions: IdLabelValue[];
  toQueryString: (
    overrides?: Record<string, string | number | boolean>
  ) => string;
  toParams: () => LeadListFilters;
  hasActiveFilters: boolean;
  statusList: StatusEntity[];
  assigneeOptions: IdLabelValue[];
  selectedAssigneeIds: string[];
  setSelectedAssigneeIds: (s: string[]) => void;
  dateRangeType: string;
  setDateRangeType: (type: string) => void;
  setDateRange: (start?: string, end?: string) => void;
};

const toOptionsFromEnum = (e: typeof LeadSource): IdLabelValue[] =>
  Object.values(e).map((val) => ({
    id: String(val),
    label: String(leadSourceOptions[val]),
    value: String(val)
  }));

const useLeadListFilters = (): UseLeadListFiltersResult => {
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);

  // UI state
  const [search, setSearch] = useState('');
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [selectedStatusIds, setSelectedStatusIds] = useState<string[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<string[]>([]);
  const [dateRangeType, setDateRangeType] = useState<string>('');
  const [selectedDateRange, setSelectedDateRange] = useState<DateRangeType>({
    startDate: '',
    endDate: ''
  });
  // Options
  const sourceOptions = useMemo(() => toOptionsFromEnum(LeadSource), []);

  const { data: statusResp } = useGetStatusOptionsQuery({ organizationId });
  const statusOptions: IdLabelValue[] = useMemo(
    () =>
      (statusResp as any[])?.map((s) => ({
        id: String(s.id),
        label: String(s.value ?? s.display_name ?? s.name ?? ''),
        value: String(s.id)
      })) || [],
    [statusResp]
  );

  const { data: tagResp } = useGetTagsQuery({ organizationId });
  const tagOptions: IdLabelValue[] = useMemo(
    () =>
      (tagResp as any[])?.map((t) => ({
        id: String(t.id),
        label: String(t.name ?? ''),
        value: String(t.id)
      })) || [],
    [tagResp]
  );

  const { data: assigneeResp } = useGetAgentsQuery({ organizationId });
  const assigneeOptions: IdLabelValue[] = useMemo(
    () =>
      (assigneeResp?.sales_agents as any[])?.map((a) => ({
        id: String(a.user_id),
        label: String(a.name ?? ''),
        value: String(a.user_id)
      })) || [],
    [assigneeResp]
  );

  const setDateRange = (start?: string, end?: string) => {
    setSelectedDateRange({
      startDate: start || '',
      endDate: end || ''
    });
  };

  const toParams = (): LeadListFilters => ({
    search: search || undefined,
    source: selectedSources.length ? selectedSources.join(',') : undefined,
    status: selectedStatusIds.length ? selectedStatusIds.join(',') : undefined,
    tags: selectedTagIds.length ? selectedTagIds.join(',') : undefined,
    assignee: selectedAssigneeIds.length
      ? selectedAssigneeIds.join(',')
      : undefined,
    start_date:
      formatISODate({
        date: selectedDateRange.startDate,
        format: 'yyyy-mm-dd'
      }) || undefined,
    end_date:
      formatISODate({
        date: selectedDateRange.endDate,
        format: 'yyyy-mm-dd'
      }) || undefined
  });

  const toQueryString = (
    overrides?: Record<string, string | number | boolean>
  ) => buildQueryString({ ...toParams(), ...(overrides || {}) });

  const hasActiveFilters = Boolean(
    search ||
      selectedSources.length ||
      selectedStatusIds.length ||
      selectedTagIds.length ||
      selectedAssigneeIds.length ||
      selectedDateRange.startDate ||
      selectedDateRange.endDate
  );

  return {
    search,
    setSearch,
    selectedSources,
    setSelectedSources,
    selectedStatusIds,
    setSelectedStatusIds,
    selectedTagIds,
    setSelectedTagIds,
    selectedAssigneeIds,
    setSelectedAssigneeIds,
    selectedDateRange,
    setDateRange,
    sourceOptions,
    statusOptions,
    tagOptions,
    toQueryString,
    toParams,
    hasActiveFilters,
    statusList: statusResp,
    assigneeOptions,
    dateRangeType,
    setDateRangeType
  };
};

export default useLeadListFilters;
